import logger from '../../Utils/logger';
import React, { useCallback, useEffect, useMemo, useState } from "react";
import styles from "../Service/Style.module.css";
import { Counter } from "../Counter/Counter";
import {
  getVoucherUnitPrice,
  useVoucherContext,
} from "../../Context/Voucher/VoucherContext";
import { BtnLink } from "../Buttons/BtnLink";
import { VoucherTermsPopup } from "../Popup/VoucherTermsPopup/VoucherTermsPopup";
import { Snakbar } from "../Snakbar/Snakbar";
import { maxVoucherCount } from "../../Utils/voucherConfig";

export const Voucher = ({
  id: categoryId,
  title: categoryName,
  description,
  image,
  vouchers = [],
  terms,
  termsAndCondition,
  onReadTerms,
}) => {
  const {
    addVoucher,
    removeVoucher,
    setVoucherList,
    voucherCount,
  } = useVoucherContext();

  const [imgError, setImgError] = useState(false);
  const [showVoucherTermsPopup, setShowVoucherTermsPopup] = useState(false);
  const [showLimitWarning, setShowLimitWarning] = useState(false);

  useEffect(() => {
    if (!showLimitWarning) return;
    if (voucherCount < maxVoucherCount) {
      setShowLimitWarning(false);
      return;
    }
    const timeout = setTimeout(() => setShowLimitWarning(false), 3000);
    return () => clearTimeout(timeout);
  }, [showLimitWarning, voucherCount]);

  const withCategoryMeta = useCallback(
    (pkg) => ({
      ...pkg,
      category_id: categoryId,
      category_name: categoryName,
    }),
    [categoryId, categoryName]
  );

  const isDisable = voucherCount >= maxVoucherCount;

  const showCounter = useCallback(
    (id, selectedPkg) => {
      try {
        const line = withCategoryMeta({
          ...selectedPkg,
          count: 1,
          isCounterVisible: true,
          price: getVoucherUnitPrice(selectedPkg),
        });
        if (!addVoucher(line)) {
          setShowLimitWarning(true);
          return;
        }
        setVoucherList((prevList) =>
          prevList.map((pkg) => ({
            ...pkg,
            vouchers: Array.isArray(pkg.vouchers)
              ? pkg.vouchers.map((voucher) =>
                  String(voucher.id) === String(id)
                    ? { ...voucher, count: 1, isCounterVisible: true }
                    : voucher
                )
              : [],
          }))
        );
      } catch (error) {
        logger.error("Error inside showCounter:", error);
      }
    },
    [addVoucher, setVoucherList, withCategoryMeta]
  );

  const incrementCounter = useCallback(
    (id, selectedPkg) => {
      const updatedPkg = withCategoryMeta({
        ...selectedPkg,
        count: selectedPkg.count + 1,
        isCounterVisible: true,
        price: getVoucherUnitPrice(selectedPkg),
      });
      if (!addVoucher(updatedPkg)) {
        setShowLimitWarning(true);
        return;
      }
      setVoucherList((prevList) =>
        prevList.map((pkg) => ({
          ...pkg,
          vouchers: Array.isArray(pkg.vouchers)
            ? pkg.vouchers.map((voucher) =>
                String(voucher.id) === String(id)
                  ? { ...voucher, count: selectedPkg.count + 1, isCounterVisible: true }
                  : voucher
              )
            : [],
        }))
      );
    },
    [addVoucher, setVoucherList, withCategoryMeta]
  );

  const decrementCounter = useCallback(
    (id, selectedPkg) => {
      if (selectedPkg.count > 0) {
        setVoucherList((prevList) =>
          prevList.map((pkg) => ({
            ...pkg,
            vouchers: Array.isArray(pkg.vouchers)
              ? pkg.vouchers.map((voucher) =>
                  String(voucher.id) === String(id)
                    ? {
                        ...voucher,
                        count: selectedPkg.count - 1,
                        isCounterVisible: selectedPkg.count - 1 > 0,
                      }
                    : voucher
                )
              : [],
          }))
        );

        const updatedPkg = withCategoryMeta({
          ...selectedPkg,
          count: selectedPkg.count - 1,
          isCounterVisible: selectedPkg.count - 1 > 0,
          price: getVoucherUnitPrice(selectedPkg),
        });
        removeVoucher(updatedPkg);
      }
    },
    [removeVoucher, setVoucherList, withCategoryMeta]
  );


  const renderedVouchers = useMemo(() => {
    if (vouchers.length === 0) {
      return <div className={styles.notFound}>Voucher not found</div>;
    }
    return vouchers.map((voucher, index) => {
      const displayCount = voucher?.count ?? 0;
      const voucherForCounter = { ...voucher, count: displayCount };
      const counterVisible = displayCount > 0 || Boolean(voucher?.isCounterVisible);
      return (
        <React.Fragment key={voucher.id}>
          <div className={styles.package}>
            <div className={styles.left}>
              <h6>{voucher?.name}</h6>
              <div className={styles.packageDetail}>
                <div className={`${styles.startingPrice} ${styles.noSeparator}`}>
                  S${voucher?.price}
                </div>
              </div>
            </div>
            <div className={styles.right}>
              <Counter
                id={voucher?.id}
                price={voucher?.price}
                isCounterVisible={counterVisible}
                disabled={isDisable}
                count={displayCount}
                handleShowCounter={() => {
                  if (!voucher.disabledBtn) {
                    showCounter(voucher?.id, voucherForCounter);
                  }
                }}
                handleIncrement={() => {
                  if (!voucher.disabledBtn) {
                    incrementCounter(voucher?.id, voucherForCounter);
                  }
                }}
                handleDecrement={() => decrementCounter(voucher?.id, voucherForCounter)}
                disabledBtn={voucher.disabledBtn ?? false}
              />
            </div>
          </div>
          {index < vouchers.length - 1 && <hr className="line" />}
        </React.Fragment>
      );
    });
  }, [
    vouchers,
    isDisable,
    showCounter,
    incrementCounter,
    decrementCounter,
  ]);

  useEffect(() => {
    const body = document.body;
    body.classList.remove("overlay", "half", "overlay-index", "overflow-hidden");
    if (showVoucherTermsPopup) {
      body.classList.add("overlay", "overflow-hidden");
    }
  }, [showVoucherTermsPopup]);

  return (
    // <div className={styles.serviceCardOuter}>
    <div>
      <Snakbar
        text={`You can add up to ${maxVoucherCount} vouchers.`}
        active={showLimitWarning}
        varient="danger"
      />
      <div className={`${styles.serviceCard} ${vouchers.length > 0 ? "" : styles.disabled}`}>
        <div className={styles.serviceTitleCard}>
          <h2 className={styles.title}>{String(categoryName || "")}</h2>
          <div className={styles.description}>Can be used to redeem for all services at all participating outlets.</div>
          <div className={styles.description}>{description}</div>
          {(terms || termsAndCondition) && (
            <div className={styles.description}>
              <BtnLink
                label="Read Terms and Conditions."
                handleClick={() => {
                  if (termsAndCondition && onReadTerms) {
                    onReadTerms(termsAndCondition);
                  } else {
                    setShowVoucherTermsPopup(true);
                  }
                }}
                title="Read Terms and Conditions"
                customClass={styles.termsLink}
              />
            </div>
          )}
        </div>
        {image && !imgError && (
          <figure style={{ cursor: "default" }}>
            <img
              src={image}
              alt={categoryName}
              className="object-cover"
              loading="lazy"
              onError={() => setImgError(true)}
            />
          </figure>
        )}
        <div className={styles.servicePackageCard}>{renderedVouchers}</div>
      </div>
      <VoucherTermsPopup
        active={showVoucherTermsPopup}
        onClose={() => setShowVoucherTermsPopup(false)}
      />
    </div>
  );
};
